import { HttpEvent, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, switchMap } from 'rxjs';

import { API_CONFIG } from '../config/api.config';
import { AuthService } from '../../features/auth/services/auth.service';
import { AuthSessionStorageService } from '../../features/auth/services/auth-session-storage.service';

export const authInterceptor: HttpInterceptorFn = (request, next) => {
  const apiConfig = inject(API_CONFIG);
  const authService = inject(AuthService);
  const authSessionStorageService = inject(AuthSessionStorageService);

  if (!isApiRequest(request.url, apiConfig.baseUrl)) {
    return next(request);
  }

  // The session endpoints authenticate via cookie/body, never a Bearer header.
  if (isAuthBypassRequest(request.url, apiConfig.auth.refresh.endpoint)) {
    return next(request);
  }

  // Attaches the current access token (resolved fresh, so it picks up a token
  // renewed by the proactive step below) and forwards the request.
  const send = (): Observable<HttpEvent<unknown>> => {
    const accessToken = resolveEffectiveAccessToken(authService, authSessionStorageService, apiConfig.auth.tokenPrefix);

    if (!accessToken) {
      return next(request);
    }

    return next(withAuthHeader(request, accessToken, apiConfig.auth.headerName, apiConfig.auth.tokenPrefix));
  };

  // Proactive renewal: if a refreshable (Cognito) access token is expired or
  // within the safety margin, renew it once — shared single-flight, so N
  // concurrent requests trigger a single /auth/refresh — BEFORE sending, so the
  // request carries a fresh token instead of relying on a 401 round-trip. On
  // refresh failure we still send with the current token; the auth-error
  // interceptor then handles the 401 + forced logout. /auth/refresh is bypassed
  // above, so this never recurses.
  if (apiConfig.auth.refresh.enabled && authService.isAccessTokenExpiringSoon() && authService.canRefreshSession()) {
    return authService.refreshSession().pipe(switchMap(() => send()));
  }

  return send();
};

function withAuthHeader(request: Parameters<HttpInterceptorFn>[0], token: string, headerName: string, tokenPrefix: string) {
  const normalizedToken = stripTokenPrefix(token, tokenPrefix);

  return request.clone({
    setHeaders: {
      [headerName]: `${tokenPrefix} ${normalizedToken}`
    }
  });
}

function resolveEffectiveAccessToken(
  authService: AuthService,
  authSessionStorageService: AuthSessionStorageService,
  tokenPrefix: string
): string | null {
  const inMemoryToken = authService.getAccessToken();

  if (inMemoryToken && inMemoryToken.trim().length > 0) {
    return stripTokenPrefix(inMemoryToken, tokenPrefix);
  }

  const persistedToken = authSessionStorageService.loadAccessToken();

  if (!persistedToken) {
    return null;
  }

  return stripTokenPrefix(persistedToken, tokenPrefix);
}

function stripTokenPrefix(token: string, tokenPrefix: string): string {
  const normalizedToken = token.trim();
  const tokenPrefixPattern = new RegExp(`^${escapeRegExp(tokenPrefix)}\\s+`, 'i');
  return normalizedToken.replace(tokenPrefixPattern, '');
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function isAuthBypassRequest(url: string, refreshEndpoint: string): boolean {
  // Login and the first-login password-set flow must never require or send an
  // Authorization header (the user has no session yet); refresh and logout
  // authenticate via the HttpOnly cookie.
  return (
    url.includes('/auth/login') ||
    url.includes('/auth/complete-new-password') ||
    url.includes('/auth/forgot-password') ||
    url.includes('/auth/confirm-forgot-password') ||
    url.includes('/auth/logout') ||
    url.includes(refreshEndpoint)
  );
}

function isApiRequest(url: string, baseUrl: string): boolean {
  const normalizedBaseUrl = baseUrl.replace(/\/$/, '');
  return url.startsWith(normalizedBaseUrl);
}
